import React, { useCallback, useEffect, useState } from 'react';
import { StatusMessage } from '../../../Hubs/HubDetails/AssignHubController/components/StatusMessage';
import { NewPatientForm } from '../../../Hubs/HubDetails/AssignHubController/components/NewPatientForm';
import { ReactComponent as UpdateSwSuccessIcon } from '../../../../images/icons/update_sw_success.svg';
import { ReactComponent as FailedToAddUserIcon } from '../../../../images/icons/adding_user_failed.svg';
import { ReactComponent as HubAssignFailedIcon } from '../../../../images/icons/hub_assign_failed.svg';
import { ReactComponent as ProfilePicErrorIcon } from '../../../../images/icons/photo_add_failed.svg';
import { Box, CircularProgress } from '@mui/material';
import { HubApiActions } from '../../../../store/hubOwner/types';
import {
  addHubOwner,
  clearHubActionStatus,
} from '../../../../store/hubOwner/actionCreators';
import {
  clearCustomerActionStatus,
  uploadProfilePicture,
} from '../../../../store/customers/actionCreators';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import {
  clearDeviceActionStatus,
  linkDeviceToOwner,
} from '../../../../store/devices/actionCreators';
import { DeviceActionType } from '../../../../shared/constants';
import { HubAssignmentConfirmation } from '../../../Dashboard/AddNewController/components/Patient/HubAssignmentConfirmation';
import HubAssignment from '../../../Dashboard/AddNewController/components/Patient/HubAssignment';

const STEPS = {
  PATIENT_DETAILS: 'patient_details',
  HUB_CONFIRMATION: 'hub_confirmation',
  HUB_ASSIGNMENT: 'hub_assignment',
  SUCCESS: 'success',
  ERROR: 'error',
};

interface AddModernFacilityPatientControllerProps {
  onClose: () => void;
  parentFacilityId: number;
  locationId?: number;
  onSuccess?: () => void;
  onStepChange: (step: string) => void;
}

const AddModernFacilityPatientController: React.FC<
  AddModernFacilityPatientControllerProps
> = ({ onClose, parentFacilityId, locationId, onSuccess, onStepChange }) => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(STEPS.PATIENT_DETAILS);
  const [newOwnerId, setNewOwnerId] = useState<number | null>(null);
  const [addPhoto, setAddPhoto] = useState<File | null>(null);
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);
  const [selectedFacilityLocationId, setSelectedFacilityLocationId] = useState<
    number | null
  >(null);
  // Duplicate-check follow-up (409 action=get_phone): re-open the form with
  // the submitted values and a now-required phone field
  const [requirePhone, setRequirePhone] = useState(false);
  const [lastSubmittedValues, setLastSubmittedValues] = useState<any | null>(
    null,
  );
  console.log({ currentStep });
  const { isAddingHubOwner, actionStatus: hubOwnerActionStatus } = useSelector(
    (state: RootState) => state.hubOwner,
  );

  const {
    actionStatus: customerActionStatus,
    isLoading: profileUploadLoading,
  } = useSelector((state: RootState) => state.customers);

  const { actionStatus: deviceActionStatus } = useSelector(
    (state: RootState) => state.devices,
  );

  const updateStep = useCallback(
    (step: string) => {
      setCurrentStep(step);
      onStepChange(step);
    },
    [onStepChange],
  );

  const handlePatientSubmit = (values: any) => {
    const { photo, isFromFacility, facility_location_id, ...patientData } =
      values;

    // Store the selected facility location ID for addig hub from parent facility
    setSelectedFacilityLocationId(facility_location_id);

    const payload = {
      ...patientData,
      facility_id: locationId || facility_location_id,
    };

    setAddPhoto(photo || null);
    setLastSubmittedValues(values);
    dispatch(addHubOwner(payload));
  };

  // Handle Hub Owner status changes
  useEffect(() => {
    if (hubOwnerActionStatus.success) {
      if (!addPhoto) {
        updateStep(STEPS.HUB_CONFIRMATION);
        setNewOwnerId(hubOwnerActionStatus.data);
      } else if (!isPhotoUploading && hubOwnerActionStatus.data) {
        setIsPhotoUploading(true);
        dispatch(uploadProfilePicture(hubOwnerActionStatus.data, addPhoto));
      }
    } else if (hubOwnerActionStatus.error) {
      if (hubOwnerActionStatus.errorAction === 'get_phone') {
        // Possible duplicate — send the user back to the form to add a
        // phone number for verification instead of showing an error screen
        dispatch(clearHubActionStatus());
        setRequirePhone(true);
        updateStep(STEPS.PATIENT_DETAILS);
      } else {
        updateStep(STEPS.ERROR);
      }
    }
  }, [hubOwnerActionStatus, addPhoto, dispatch, isPhotoUploading, updateStep]);

  // Handle Profile Picture upload status
  useEffect(() => {
    if (customerActionStatus.success && isPhotoUploading) {
      updateStep(STEPS.HUB_CONFIRMATION);
      setNewOwnerId(hubOwnerActionStatus.data);
      setIsPhotoUploading(false);
      setAddPhoto(null);
    } else if (customerActionStatus.error && isPhotoUploading) {
      updateStep(STEPS.ERROR);
      setIsPhotoUploading(false);
    }
  }, [
    customerActionStatus,
    hubOwnerActionStatus.data,
    isPhotoUploading,
    updateStep,
  ]);

  const handleClearCatch = useCallback(() => {
    dispatch(clearCustomerActionStatus());
    dispatch(clearHubActionStatus());
    dispatch(clearDeviceActionStatus());
    setIsPhotoUploading(false);
    setAddPhoto(null);
    setRequirePhone(false);
    setLastSubmittedValues(null);
    if (onSuccess) {
      onSuccess();
    }
    onClose();
  }, [dispatch, onClose, onSuccess]);

  const handleHubConfirmation = (wantHub: boolean) => {
    if (wantHub) {
      updateStep(STEPS.HUB_ASSIGNMENT);
    } else {
      setNewOwnerId(null);
      setSelectedFacilityLocationId(null);
      // If no hub is selected, we can consider the process successful
      // as the patient is added without a hub.
      updateStep(STEPS.SUCCESS);
    }
  };

  const handleHubAssignment = (hubId: number) => {
    if (!newOwnerId) return;
    dispatch(linkDeviceToOwner(hubId, newOwnerId));
  };

  useEffect(() => {
    if (deviceActionStatus.type === DeviceActionType.LinkDeviceToOwner) {
      if (deviceActionStatus.success) {
        updateStep(STEPS.SUCCESS);
      } else {
        updateStep(STEPS.ERROR);
      }
    }
  }, [deviceActionStatus, updateStep]);

  const renderStep = () => {
    if (isAddingHubOwner || profileUploadLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress />
        </Box>
      );
    }

    switch (currentStep) {
      case STEPS.PATIENT_DETAILS:
        return (
          <NewPatientForm
            onAdd={handlePatientSubmit}
            onCancel={onClose}
            isFromFacility={true}
            setCurrentStep={(step: string) => {
              if (step === 'new_patient') {
                onStepChange(STEPS.PATIENT_DETAILS);
              } else {
                onStepChange('initial');
              }
            }}
            initialValues={{
              facility_id: parentFacilityId,
              facility_location_id: locationId,
            }}
            requirePhone={requirePhone}
            prefillValues={lastSubmittedValues}
          />
        );
      case STEPS.HUB_CONFIRMATION:
        return (
          <HubAssignmentConfirmation
            onConfirm={handleHubConfirmation}
            onBack={() => updateStep(STEPS.PATIENT_DETAILS)}
          />
        );
      case STEPS.HUB_ASSIGNMENT:
        return (
          <HubAssignment
            onSubmit={handleHubAssignment}
            onBack={() => updateStep(STEPS.HUB_CONFIRMATION)}
            facilityId={locationId || selectedFacilityLocationId!}
            isFromModernFacility
          />
        );
      case STEPS.SUCCESS:
      case STEPS.ERROR:
        return (
          <StatusMessage
            icon={
              currentStep === STEPS.SUCCESS ? (
                <UpdateSwSuccessIcon />
              ) : hubOwnerActionStatus.apiAction ===
                HubApiActions.ADD_HUB_OWNER ? (
                <FailedToAddUserIcon />
              ) : isPhotoUploading ? (
                <ProfilePicErrorIcon />
              ) : (
                <HubAssignFailedIcon />
              )
            }
            title={
              currentStep === STEPS.SUCCESS
                ? 'Added successfully!'
                : hubOwnerActionStatus.apiAction === HubApiActions.ADD_HUB_OWNER
                  ? "Patient didn't save"
                  : isPhotoUploading
                    ? "Photo didn't save"
                    : 'Failed to assign Hub'
            }
            message={
              currentStep === STEPS.ERROR
                ? hubOwnerActionStatus.errorAction === 'contact_cc'
                  ? 'Something went wrong. Please contact customer care.'
                  : 'Please try again or come back later.'
                : undefined
            }
            onClose={() => {
              if (
                hubOwnerActionStatus.error &&
                hubOwnerActionStatus.apiAction === HubApiActions.ADD_HUB_OWNER
              ) {
                // If error is from adding patient, go back to patient details
                updateStep(STEPS.PATIENT_DETAILS);
                dispatch(clearHubActionStatus());
              } else {
                // Otherwise clear everything and close modal
                handleClearCatch();
              }
            }}
            primaryButtonText={
              currentStep === STEPS.ERROR ? 'Try again' : undefined
            }
          />
        );
      default:
        return null;
    }
  };

  return renderStep();
};

export default AddModernFacilityPatientController;
